<template>
  <div class="dp-auth-page">
    <div class="dp-auth-card">
      <h2>일일작업 허가서 작성</h2>
      <p class="text-888 mt-5">
        발주 담당자 정보와 작업 책임자 이름, 작업계획번호로 본인 인증 후 작성 대상 안전작업허가서를 선택해 주세요.
      </p>

      <!-- 1단계: 인증 폼 -->
      <div class="mt-20">
        <div class="form-group">
          <label>발주 담당자 이름 (네이버 임직원)</label>
          <input type="text" class="form-control"
                 v-model.trim="form.ordererName"
                 placeholder="발주 담당자 이름"
                 @keyup.enter="searchPermits()">
        </div>
        <div class="form-group mt-10">
          <label>발주 담당자 이메일</label>
          <input type="email" class="form-control"
                 v-model.trim="form.ordererEmail"
                 placeholder="orderer@navercorp.com"
                 @keyup.enter="searchPermits()">
        </div>
        <div class="form-group mt-10">
          <label>작업 책임자 이름</label>
          <input type="text" class="form-control"
                 v-model.trim="form.managerName"
                 placeholder="작업 책임자 이름"
                 @keyup.enter="searchPermits()">
        </div>
        <div class="form-group mt-10">
          <label>작업계획번호</label>
          <input type="text" class="form-control"
                 v-model.trim="form.planId"
                 placeholder="예: 세종-20260707-0001"
                 @keyup.enter="searchPermits()">
        </div>
        <div class="mt-15 text-center">
          <button class="btn btn-md blue" :disabled="searching || !canSearch" @click="searchPermits()">
            <span v-if="searching">검색 중...</span>
            <span v-else>안전작업허가서 검색</span>
          </button>
        </div>
      </div>

      <!-- 2단계: 검색 결과 -->
      <div v-if="searched" class="mt-30">
        <div class="dp-section-title">
          승인된 안전작업허가서
          <span class="text-888 ml-5">({{ results.length }}건)</span>
        </div>

        <div v-if="results.length === 0" class="text-center text-888 mt-15 pv-30">
          매칭되는 안전작업허가서가 없습니다.<br>
          발주 담당자 정보·작업 책임자 이름·작업계획번호를 다시 확인해 주세요.
        </div>

        <ul v-else class="dp-permit-list">
          <li v-for="r in results" :key="r.planId" class="dp-permit-item" @click="selectPermit(r)">
            <div class="dp-permit-header">
              <span class="dp-plan-id">{{ r.planId }}</span>
              <span class="tag success ml-10">승인</span>
            </div>
            <div class="dp-permit-name">{{ r.planName || '-' }}</div>
            <div class="dp-permit-meta text-888">
              <span>업체 {{ r.companyName || '-' }}</span>
              <span class="ml-10">·</span>
              <span class="ml-10">{{ formatPeriod(r.fromDate, r.toDate) }}</span>
              <span class="ml-10">·</span>
              <span class="ml-10">{{ idcNameOf(r.idcCode) }}</span>
            </div>
            <div class="dp-permit-action">
              <i class="icon-arrow-line-right"></i>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'DailyPermitAuth',
  data() {
    return {
      form: { ordererName: '', ordererEmail: '', managerName: '', planId: '' },
      searching: false,
      searched: false,
      results: [],
      locationMap: {}
    };
  },
  computed: {
    canSearch() {
      return !!this.form.ordererName && this.isValidEmail(this.form.ordererEmail)
        && !!this.form.managerName && !!this.form.planId;
    }
  },
  mounted() {
    this.loadLocations();
  },
  methods: {
    async loadLocations() {
      try {
        const res = await this.$http.get('/api/public/safety-location/idcList');
        const nodes = (res && res.data) || [];
        const map = {};
        const walk = (arr) => {
          (arr || []).forEach((node) => {
            if (!node) return;
            if (node.isDeleteYN && node.isDeleteYN !== 'N') return;
            map[node.locationCode] = node.locationName;
            if (node.children && node.children.length) walk(node.children);
          });
        };
        walk(nodes);
        this.locationMap = map;
      } catch (e) {
        // 위치 매핑 실패해도 검색은 진행 가능
        console.log('safety-location 로드 실패', e);
      }
    },
    async searchPermits() {
      if (!this.canSearch || this.searching) return;
      this.searching = true;
      this.searched = false;
      try {
        const res = await this.$http.get('/api/public/daily-permit/work-permits', {
          params: {
            ordererName: this.form.ordererName,
            ordererEmail: this.form.ordererEmail,
            managerName: this.form.managerName,
            planId: this.form.planId
          }
        });
        const data = (res && res.data && (res.data.payload || res.data)) || {};
        // 신규 응답: { permits: [...], token }. (구버전: 배열)
        this.results = Array.isArray(data) ? data : (data.permits || []);
        // 인증 성공 시 발급된 접근 토큰 저장 → 이후 작성/조회/제출 API 에 X-AUTH-TOKEN 자동 부착
        if (!Array.isArray(data) && data.token) {
          this.$store.commit('auth/setAuth', data.token);
        }
      } catch (e) {
        console.log('daily-permit 검색 실패', e);
        this.noti('검색 오류', '안전작업허가서 검색 중 오류가 발생했습니다.', 'danger');
        this.results = [];
      } finally {
        this.searching = false;
        this.searched = true;
      }
    },
    selectPermit(r) {
      if (!r || !r.planId) return;
      // Phase 2: 일일허가서 작성 페이지로 이동
      this.$router.push({
        path: '/daily-permit/input',
        query: { parentPlanId: r.planId }
      });
    },
    idcNameOf(code) {
      if (!code) return '-';
      return this.locationMap[code] || code;
    },
    formatPeriod(from, to) {
      if (!from && !to) return '-';
      const f = from ? moment(Number(from)).format('YYYY-MM-DD') : '';
      const t = to ? moment(Number(to)).format('YYYY-MM-DD') : '';
      return f && t ? `${f} ~ ${t}` : (f || t);
    },
    formatPhone(value) {
      if (!value) return '';
      const digits = String(value).replace(/[^0-9]/g, '').slice(0, 11);
      if (digits.length < 4) return digits;
      if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    },
    isValidPhone(p) {
      if (!p) return false;
      return /^01[016789]-\d{3,4}-\d{4}$/.test(p);
    },
    isValidEmail(e) {
      if (!e) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    }
  }
};
</script>

<style scoped>
.dp-auth-page {
  display: flex;
  justify-content: center;
  padding: 40px 16px;
  min-height: 100vh;
  background: #f5f7fa;
}
.dp-auth-card {
  width: 100%;
  max-width: 720px;
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  padding: 32px 36px;
}
.dp-auth-card h2 {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}
.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  display: block;
  margin-bottom: 4px;
}
.dp-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e2e2e2;
  padding-bottom: 6px;
  margin-bottom: 10px;
}
.dp-permit-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.dp-permit-item {
  position: relative;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  padding: 12px 40px 12px 14px;
  margin-bottom: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
}
.dp-permit-item:hover {
  border-color: #1565c0;
  box-shadow: 0 2px 6px rgba(21, 101, 192, 0.08);
}
.dp-permit-header {
  display: flex;
  align-items: center;
}
.dp-plan-id {
  font-weight: 600;
  color: #1565c0;
}
.dp-permit-name {
  margin-top: 4px;
  font-size: 13px;
  color: #333;
}
.dp-permit-meta {
  margin-top: 4px;
  font-size: 12px;
}
.dp-permit-action {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}
.pv-30 { padding-top: 30px; padding-bottom: 30px; }
</style>